import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { SectionCard, StatCard, EmptyState, ConfirmDialog, currency, number } from './ui';
import { TRUE_COLORS } from '../theme';

type Draft = { name: string; address: string; email: string; phone: string; no: number };

const LocationDetail = () => {
  const { id } = useParams();
  const axios = useAxios();
  const navigate = useNavigate();

  const [location, setLocation] = React.useState<any>(null);
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [summary, setSummary] = React.useState<any>(null);
  const [lowStock, setLowStock] = React.useState<number | null>(null);
  const [locationCount, setLocationCount] = React.useState(0);
  const [notFound, setNotFound] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const toDraft = (l: any): Draft => ({ name: l.name ?? '', address: l.address ?? '', email: l.email ?? '', phone: l.phone ?? '', no: (l.checkouts ?? []).length || 1 });

  React.useEffect(() => {
    (async () => {
      const [l, s, inv, all] = await Promise.allSettled([
        axios.get(`api/locations/${id}/`),
        axios.get('api/receipts/summary/', { params: { location: id, start_date: dayjs().startOf('month').utc().format(), end_date: dayjs().endOf('month').utc().format() } }),
        axios.get('inventory/inventory/', { params: { location_id: id } }),
        axios.get('api/locations/'),
      ]);
      if (l.status === 'fulfilled') { setLocation(l.value.data); setDraft(toDraft(l.value.data)); } else setNotFound(true);
      if (s.status === 'fulfilled') setSummary(s.value.data?.[0] ?? null);
      if (inv.status === 'fulfilled') {
        setLowStock((inv.value.data ?? []).filter((i: any) => {
          const q = Number(i.quantity) || 0;
          const r = Number(i.restock_quantity) || 0;
          return q <= 0 || (r > 0 && q <= r);
        }).length);
      }
      if (all.status === 'fulfilled') setLocationCount((all.value.data ?? []).length);
    })();
  }, [axios, id]);

  if (notFound) {
    return (
      <PageShell back={{ to: '/admin/organization', label: 'Organización' }}>
        <EmptyState title="Sucursal no encontrada" />
      </PageShell>
    );
  }

  const dirty = location && draft && JSON.stringify(toDraft(location)) !== JSON.stringify(draft);

  const save = async () => {
    if (!draft?.name.trim()) { setMessage({ type: 'error', text: 'El nombre es obligatorio.' }); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`api/locations/${id}/`, {
        name: draft.name.trim(), address: draft.address.trim(), email: draft.email.trim() || null, phone: draft.phone.trim() || null, no: draft.no,
      });
      setLocation(data);
      setDraft(toDraft(data));
      setMessage({ type: 'success', text: 'Cambios guardados.' });
    } catch (err: any) {
      const d = err?.response?.data;
      setMessage({ type: 'error', text: d?.response ?? (typeof d === 'object' ? Object.values(d).flat().join(' ') : 'No se pudieron guardar los cambios.') });
    } finally {
      setSaving(false);
    }
  };

  const set = (k: keyof Omit<Draft, 'no'>) => (e: React.ChangeEvent<HTMLInputElement>) => setDraft(d => d && { ...d, [k]: e.target.value });

  return (
    <PageShell
      back={{ to: '/admin/organization', label: 'Organización' }}
      title={location ? location.name : <Skeleton width={240} />}
      subtitle={location?.address || 'Sucursal'}
      actions={
        <>
          {dirty && <Button color="inherit" onClick={() => setDraft(toDraft(location))}>Descartar</Button>}
          <Button variant="contained" onClick={save} disabled={!dirty || saving}>Guardar cambios</Button>
        </>
      }
    >
      {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 3 }}>{message.text}</Alert>}
      <Grid container spacing={3}>
        {[
          { label: 'Ingresos del mes', value: currency(summary?.total_amount), icon: <PaymentsOutlinedIcon /> },
          { label: 'Ventas del mes', value: number(summary?.total_count), icon: <ReceiptLongOutlinedIcon /> },
          { label: 'Ticket promedio', value: currency(summary?.avg), icon: <ShoppingBasketOutlinedIcon /> },
          { label: 'Productos por reabastecer', value: lowStock == null ? '—' : number(lowStock), icon: <WarningAmberOutlinedIcon />, onClick: () => navigate('/admin/inventory') },
        ].map(card => (
          <Grid key={card.label} size={{ xs: 6, sm: 6, lg: 3 }}>
            <StatCard label={card.label} value={card.value} icon={card.icon} loading={!location} onClick={card.onClick} />
          </Grid>
        ))}

        <Grid size={{ xs: 12, lg: 8 }}>
          <SectionCard title="Datos de la sucursal" subtitle="Aparecen en los tickets impresos por TRUE POS">
            {draft ? (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}><TextField label="Nombre" required fullWidth value={draft.name} onChange={set('name')} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><TextField label="Teléfono" type="tel" fullWidth value={draft.phone} onChange={set('phone')} /></Grid>
                <Grid size={12}><TextField label="Dirección" fullWidth value={draft.address} onChange={set('address')} /></Grid>
                <Grid size={12}><TextField label="Correo" type="email" fullWidth value={draft.email} onChange={set('email')} /></Grid>
              </Grid>
            ) : <Skeleton variant="rounded" height={180} />}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            <SectionCard title="Cajas" subtitle="Cada computadora con TRUE POS usa una caja">
              {draft && (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton onClick={() => setDraft(d => d && { ...d, no: Math.max(1, d.no - 1) })} disabled={draft.no <= 1} sx={{ border: `1px solid ${TRUE_COLORS.borderSolid}` }}>
                      <RemoveIcon />
                    </IconButton>
                    <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                      <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>{draft.no}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{draft.no === 1 ? 'caja' : 'cajas'}</Typography>
                    </Box>
                    <IconButton onClick={() => setDraft(d => d && { ...d, no: d.no + 1 })} sx={{ border: `1px solid ${TRUE_COLORS.borderSolid}` }}>
                      <AddIcon />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, justifyContent: 'center' }}>
                    {Array.from({ length: draft.no }, (_, i) => (
                      <Chip key={i} icon={<PointOfSaleOutlinedIcon />} label={`Caja ${i + 1}`} variant={i < (location?.checkouts ?? []).length ? 'filled' : 'outlined'} />
                    ))}
                  </Stack>
                </Stack>
              )}
            </SectionCard>

            <SectionCard title="Zona de riesgo">
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {locationCount <= 1
                  ? 'Esta es tu única sucursal; no se puede eliminar.'
                  : 'Eliminar la sucursal borra su inventario y sus cajas. Las ventas registradas se conservan.'}
              </Typography>
              <Tooltip title={locationCount <= 1 ? 'Necesitas al menos una sucursal' : ''}>
                <span>
                  <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} disabled={locationCount <= 1} onClick={() => setConfirmDelete(true)}>
                    Eliminar sucursal
                  </Button>
                </span>
              </Tooltip>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmDelete}
        danger
        title={`¿Eliminar ${location?.name}?`}
        description="Se borrarán su inventario y sus cajas. Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await axios.delete(`api/locations/${id}/`);
          navigate('/admin/organization', { replace: true });
        }}
      />
    </PageShell>
  );
};

export default LocationDetail;
